import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useCanEnterSurface } from '@/lib/platform/surfaces';
import { leaveAfterSignOut } from '@/lib/platform/routing';
const links = [
  { to: '/clients', label: 'Clients', page: SURFACES.clients },
  { to: '/filings', label: 'Filings', page: SURFACES.filings },
  { to: '/filing-status', label: 'Status', page: SURFACES['filing-status'] },
  { to: '/client-register', label: 'Client register', page: SURFACES['client-register'] },
  { to: '/tax-rules', label: 'Tax rules', page: SURFACES['tax-rules'] },
  { to: '/filing-credentials', label: 'Credentials', page: SURFACES['filing-credentials'] },
  { to: '/approval-queue', label: 'Approvals', page: SURFACES['approval-queue'] },
] as const;
export function AppShell({ children, title }: { children: ReactNode; title?: string; activePath?: string }) {
  const path = useRouterState({ select: (state) => state.location.pathname });
  return (
    <div className="min-h-screen">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-4">
          <Link to="/clients" className="mr-auto font-semibold tracking-tight">
            FiduciaryFile
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {links.map((link) => (
              <NavLink key={link.to} {...link} current={path === link.to} />
            ))}
          </nav>
          <Button variant="outline" size="sm" data-jr-key="sign-out" onClick={() => void leaveAfterSignOut()}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-8">
        {title ? (
          <p className="mb-6 text-xs font-medium uppercase tracking-[.16em] text-muted-foreground">{title}</p>
        ) : null}
        {children}
      </main>
    </div>
  );
}
function NavLink({
  to,
  label,
  page,
  current,
}: {
  to: (typeof links)[number]['to'];
  label: string;
  page: string;
  current: boolean;
}) {
  const allowed = useCanEnterSurface(page);
  return allowed ? (
    <Link
      to={to}
      data-jr-key={`${label.toLowerCase().replaceAll(' ', '-')}-nav-link`}
      aria-current={current ? 'page' : undefined}
      className="px-2 py-1 text-sm text-muted-foreground hover:text-foreground"
    >
      {label}
    </Link>
  ) : null;
}
